import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
// PROMPT 144: el color de la marca en el chat.
import { COLOR_OTRAS_MARCAS, esColorValido } from "@/lib/marcasWa";
import { toast } from "sonner";
import MensajesAutomaticos from "@/components/agente/MensajesAutomaticos"; // PROMPT 150
import { BotonPerfilWhatsApp } from "@/components/agente/PerfilWhatsAppDialog";
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  ClipboardCheck,
  Info,
  Loader2,
  Plug,
  Radio,
  RefreshCw,
  Save,
  ShieldCheck,
  Sparkles,
  XCircle,
} from "lucide-react";

// PROMPT 94 (2026-09-08): configuracion del canal real de WhatsApp.
// Cada marca tiene su numero; Meta lo identifica con un "phone number id". Ese id es lo
// que permite saber por que marca escribe el cliente. El token NUNCA se carga aca: vive en
// una variable de entorno y esta pantalla solo dice si esta cargada o no.
//
// PROMPT 127 (2026-10-13; las rutas son de los PROMPTS 125 y 126): todo lo necesario para dejar el agente listo para prender:
//  - modo "Prueba" (el agente contesta solo a los numeros de prueba) y la lista de numeros a
//    los que el agente no les contesta nunca (agencias, guias, proveedores, equipo);
//  - "Verificar con Meta": si el numero cargado es el real o el de prueba, su calidad, si Meta
//    le manda los mensajes a la plataforma y como estan las plantillas (solo lectura);
//  - "Conectar": suscribe la app de la plataforma a la cuenta de WhatsApp (no prende el agente);
//  - "Crear la plantilla del recordatorio" en Meta;
//  - "Lista para prender": todo lo que tiene que estar bien, con lo que falta;
//  - "Probar la IA": si la IA contesta ahora (y por que no, si no contesta).
// Guardar, conectar y crear la plantilla son solo para administradores (el servidor lo exige).

// PROMPT 119: dias del horario de atencion, 0 = lunes ... 6 = domingo (igual que el backend).
const DIAS = ["L", "M", "X", "J", "V", "S", "D"];
// PROMPT 120: el dia de hoy en Nueva York, para decir con que hora se decide si contesta.
const DIAS_LARGOS = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"];
const CALIDAD = { GREEN: "alta", YELLOW: "media", RED: "baja" };
const ESTADO_PLANTILLA = { APPROVED: "aprobada", PENDING: "en revisión", REJECTED: "rechazada", PAUSED: "pausada", DISABLED: "desactivada" };
const PUNTO = {
  ok: { Icono: CheckCircle2, cls: "text-emerald-600" },
  falta: { Icono: XCircle, cls: "text-red-600" },
  aviso: { Icono: AlertTriangle, cls: "text-amber-600" },
  info: { Icono: Info, cls: "text-slate-500" },
};

// Un numero por renglon (tambien sirven comas o punto y coma).
const aLista = (txt) => (txt || "").split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);

function Chip({ ok, children }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium border ${
        ok
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-slate-100 text-slate-600 border-slate-200"
      }`}
    >
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
      {children}
    </span>
  );
}

function Fila({ estado, titulo, children }) {
  const p = PUNTO[estado] || PUNTO.info;
  return (
    <div className="flex items-start gap-2 text-xs">
      <p.Icono className={`h-4 w-4 shrink-0 mt-px ${p.cls}`} />
      <div className="min-w-0">
        <span className="font-medium text-slate-800">{titulo}</span>
        {children ? <span className="text-slate-600"> · {children}</span> : null}
      </div>
    </div>
  );
}

// PROMPT 127: lo que contesto Meta del numero de la marca.
function PanelMeta({ meta, isAdmin, conectando, creando, onConectar, onCrearPlantilla, slug }) {
  const [confirmar, setConfirmar] = useState(false);
  if (!meta) return null;
  const n = meta.numero;
  const plantilla = (idioma) =>
    (meta.plantillas || []).find((t) => t.nombre === "recordatorio_tour" && t.idioma === idioma)?.estado || null;
  const faltaPlantilla = meta.plantillas && (!plantilla("es") || !plantilla("en"));
  const verificacion = meta.cuenta?.verificacion_empresa;
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2 space-y-1.5" data-testid={`canal-meta-${slug}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Lo que dice Meta</p>
      {n ? (
        <Fila estado={meta.es_de_prueba ? "falta" : n.calidad === "RED" || n.calidad === "YELLOW" ? "aviso" : "ok"} titulo="Número">
          {n.numero} · {n.nombre || "sin nombre"} · calidad {CALIDAD[n.calidad] || (n.calidad || "sin dato").toLowerCase()}
          {n.coexistencia ? " · también en la app del celular" : ""}
          {meta.es_de_prueba ? " · es el número de PRUEBA de Meta" : ""}
        </Fila>
      ) : null}
      {n?.puede_enviar ? (
        <Fila estado={n.puede_enviar === "AVAILABLE" ? "ok" : n.puede_enviar === "LIMITED" ? "aviso" : "falta"} titulo="Envío">
          {n.puede_enviar === "AVAILABLE" ? "sin restricciones" : n.puede_enviar === "LIMITED" ? "con límites" : "bloqueado"}
          {(n.avisos || []).length ? ` · ${n.avisos.join(" ")}` : ""}
        </Fila>
      ) : null}
      {meta.waba_id ? (
        <div className="space-y-1">
          <Fila
            estado={meta.suscripta === true ? "ok" : meta.suscripta === false ? "falta" : "aviso"}
            titulo="Meta le manda los mensajes a la plataforma"
          >
            {meta.suscripta === true
              ? "sí"
              : meta.suscripta === false
              ? `todavía no${(meta.apps_suscriptas || []).length ? ` (hoy: ${meta.apps_suscriptas.map((a) => a.nombre).join(", ")})` : ""}`
              : "no se pudo confirmar"}
          </Fila>
          {meta.suscripta === false && !meta.es_de_prueba ? (
            confirmar ? (
              <div className="ml-6 rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900 space-y-1.5" data-testid={`canal-conectar-confirmar-${slug}`}>
                <p>
                  Desde que conectes, todos los mensajes que entren a este número también van a entrar a la plataforma.
                  El agente queda como está ahora (no se prende) y en el celular no cambia nada.
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="h-7 gap-1.5"
                    disabled={conectando}
                    onClick={() => {
                      setConfirmar(false);
                      onConectar();
                    }}
                    data-testid={`canal-conectar-si-${slug}`}
                  >
                    {conectando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-3.5 w-3.5" />}
                    Sí, conectar
                  </Button>
                  <Button size="sm" variant="outline" className="h-7" onClick={() => setConfirmar(false)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="ml-6 h-7 gap-1.5"
                disabled={!isAdmin || conectando}
                title={isAdmin ? "Suscribe la app de la plataforma a la cuenta de WhatsApp" : "Solo un administrador"}
                onClick={() => setConfirmar(true)}
                data-testid={`canal-conectar-${slug}`}
              >
                <Plug className="h-3.5 w-3.5" /> Conectar
              </Button>
            )
          ) : null}
        </div>
      ) : null}
      {meta.plantillas ? (
        <div className="space-y-1">
          <Fila estado={plantilla("es") === "APPROVED" && plantilla("en") === "APPROVED" ? "ok" : "info"} titulo="Plantilla del recordatorio">
            es: {ESTADO_PLANTILLA[plantilla("es")] || (plantilla("es") ? plantilla("es").toLowerCase() : "no está creada")} · en:{" "}
            {ESTADO_PLANTILLA[plantilla("en")] || (plantilla("en") ? plantilla("en").toLowerCase() : "no está creada")}
          </Fila>
          {faltaPlantilla && !meta.es_de_prueba ? (
            <Button
              size="sm"
              variant="outline"
              className="ml-6 h-7 gap-1.5"
              disabled={!isAdmin || creando}
              title={isAdmin ? "La da de alta en Meta con el mismo texto que manda la plataforma" : "Solo un administrador"}
              onClick={onCrearPlantilla}
              data-testid={`canal-plantilla-${slug}`}
            >
              {creando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Crear la plantilla en Meta
            </Button>
          ) : null}
        </div>
      ) : null}
      {verificacion ? (
        <Fila estado={verificacion === "verified" ? "ok" : "aviso"} titulo="Empresa">
          {verificacion === "verified" ? "verificada en Meta" : "sin verificar en Meta"}
        </Fila>
      ) : null}
      {(meta.problemas || []).map((p) => (
        <Fila key={p} estado="falta" titulo="Problema">
          {p}
        </Fila>
      ))}
    </div>
  );
}

// PROMPT 127: la lista para prender el agente.
function PanelLista({ lista, slug }) {
  if (!lista) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2 space-y-1.5" data-testid={`canal-lista-${slug}`}>
      <div
        className={`rounded px-2 py-1 text-xs font-medium ${
          lista.listo ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"
        }`}
        data-testid={`canal-lista-veredicto-${slug}`}
      >
        {lista.listo
          ? "Lista para prender: lo único que queda es elegir cuándo contesta el agente y guardar."
          : `Antes de prender falta${lista.faltan === 1 ? "" : "n"} ${lista.faltan} cosa${lista.faltan === 1 ? "" : "s"}.`}
        {lista.avisos ? ` ${lista.avisos} aviso${lista.avisos === 1 ? "" : "s"} para mejorar.` : ""}
      </div>
      {(lista.puntos || []).map((p) => (
        <Fila key={p.clave} estado={p.estado} titulo={p.titulo}>
          {p.detalle}
        </Fila>
      ))}
    </div>
  );
}

// PROMPT 148: consumo del mes de una marca. Lee solo lo que ya guardamos de
// cada mensaje (GET a nuestra base): no le pregunta nada a Meta ni a YCloud.
const ETIQUETAS_CONSUMO = {
  servicio_gratis: "Conversaciones de atención (gratis)",
  servicio_cobrado: "Conversaciones de atención (cobradas)",
  utility: "Plantillas de utilidad",
  marketing: "Plantillas de marketing",
  autenticacion: "Plantillas de autenticación",
  sin_dato: "Sin dato de Meta todavía",
};

function mesDeHoy(desfase) {
  const hoy = new Date();
  const f = new Date(hoy.getFullYear(), hoy.getMonth() + desfase, 1);
  return `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, "0")}`;
}

function PanelConsumo({ slug }) {
  const [abierto, setAbierto] = useState(false);
  const [mes, setMes] = useState(() => mesDeHoy(0));
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!abierto) return undefined;
    let vivo = true;
    setCargando(true);
    setError("");
    api
      .get(`/whatsapp/canal/${slug}/consumo`, { params: { mes } })
      .then((r) => {
        if (vivo) setDatos(r.data);
      })
      .catch((e) => {
        if (!vivo) return;
        setDatos(null);
        setError(formatApiError(e.response?.data?.detail) || "No se pudo leer el consumo.");
      })
      .finally(() => {
        if (vivo) setCargando(false);
      });
    return () => {
      vivo = false;
    };
  }, [abierto, mes, slug]);

  const cats = datos?.por_categoria || {};
  const actual = mesDeHoy(0);
  const anterior = mesDeHoy(-1);
  return (
    <div
      className="rounded-md border border-slate-200 p-2 space-y-2"
      data-testid={`canal-consumo-${slug}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label className="text-xs text-slate-600">Consumo del mes</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setAbierto((v) => !v)}
          data-testid={`canal-consumo-abrir-${slug}`}
        >
          {abierto ? "Ocultar" : "Ver consumo"}
        </Button>
      </div>
      {abierto && (
        <div className="space-y-2 text-xs text-slate-700">
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={mes === actual ? "default" : "outline"}
              onClick={() => setMes(actual)}
              data-testid={`canal-consumo-mes-actual-${slug}`}
            >
              Este mes
            </Button>
            <Button
              type="button"
              size="sm"
              variant={mes === anterior ? "default" : "outline"}
              onClick={() => setMes(anterior)}
              data-testid={`canal-consumo-mes-anterior-${slug}`}
            >
              Mes anterior
            </Button>
          </div>
          {cargando && (
            <p className="flex items-center gap-1 text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" /> Leyendo el consumo…
            </p>
          )}
          {error && (
            <p className="text-red-600" data-testid={`canal-consumo-error-${slug}`}>
              {error}
            </p>
          )}
          {!cargando && datos && (
            <>
              <p data-testid={`canal-consumo-total-${slug}`}>
                <strong>{datos.total}</strong> mensajes en {datos.mes}
              </p>
              <ul className="space-y-0.5">
                {Object.keys(ETIQUETAS_CONSUMO).map((k) => (
                  <li
                    key={k}
                    className="flex justify-between gap-2"
                    data-testid={`canal-consumo-cat-${slug}-${k}`}
                  >
                    <span>{ETIQUETAS_CONSUMO[k]}</span>
                    <span className="font-medium">{cats[k] || 0}</span>
                  </li>
                ))}
              </ul>
              {datos.fallidos > 0 && (
                <p className="text-amber-700" data-testid={`canal-consumo-fallidos-${slug}`}>
                  {datos.fallidos} no llegaron (fallidos).
                </p>
              )}
              {datos.nota && (
                <p className="text-[11px] text-slate-500" data-testid={`canal-consumo-nota-${slug}`}>
                  {datos.nota}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function CanalWhatsAppDialog({ open, onOpenChange, onCambio }) {
  const { isAdmin } = useAuth();
  const [datos, setDatos] = useState(null);
  const [borrador, setBorrador] = useState({});
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(null);
  // PROMPT 127: lo que contesto Meta y la lista para prender, por marca.
  const [metaPor, setMetaPor] = useState({});
  const [listaPor, setListaPor] = useState({});
  const [ocupado, setOcupado] = useState({}); // {slug: "meta" | "lista" | "conectar" | "plantilla"}
  const [ia, setIa] = useState(null);
  const [probandoIa, setProbandoIa] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/canal");
      setDatos(data);
      const b = {};
      (data?.marcas || []).forEach((m) => {
        b[m.slug] = {
          wa_phone_number_id: m.wa_phone_number_id || "",
          wa_display_number: m.wa_display_number || "",
          wa_waba_id: m.wa_waba_id || "",
          wa_activo: !!m.wa_activo,
          // PROMPT 144: el color en el chat (el ya resuelto) y la coexistencia (true, false o sin dato).
          wa_color: m.wa_color || "",
          wa_coexistencia: typeof m.wa_coexistencia === "boolean" ? m.wa_coexistencia : null,
          // PROMPT 148: avisar leido y escribiendo (apagado por defecto).
          wa_marcar_leido: !!m.wa_marcar_leido,
          wa_bienvenida: m.wa_bienvenida || { activa: false, texto_es: "", texto_en: "" },
          wa_ausencia: m.wa_ausencia || { activa: false, texto_es: "", texto_en: "" },
          // PROMPT 119: cuando contesta el agente solo, y el horario del equipo.
          wa_agente_modo: m.wa_agente_modo || "nunca",
          wa_horario_desde: m.wa_horario_desde || "09:00",
          wa_horario_hasta: m.wa_horario_hasta || "18:00",
          wa_horario_dias: m.wa_horario_dias || [0, 1, 2, 3, 4],
          // PROMPT 127: un numero por renglon.
          numeros_prueba_txt: (m.wa_numeros_prueba || []).join("\n"),
          no_contestar_txt: (m.wa_no_contestar || []).join("\n"),
        };
      });
      setBorrador(b);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) cargar();
  }, [open, cargar]);

  const ocupar = (slug, que) => setOcupado((o) => ({ ...o, [slug]: que }));

  const guardar = async (slug) => {
    setGuardando(slug);
    try {
      const { numeros_prueba_txt, no_contestar_txt, ...resto } = borrador[slug] || {};
      // PROMPT 144: el color solo se manda si se cambio (asi no se guarda sin querer el de la tabla) y la
      // coexistencia solo si se marco o se desmarco.
      const original = (datos?.marcas || []).find((x) => x.slug === slug) || {};
      if (!resto.wa_color || resto.wa_color.toUpperCase() === String(original.wa_color || "").toUpperCase()) {
        delete resto.wa_color;
      }
      if (typeof resto.wa_coexistencia !== "boolean") delete resto.wa_coexistencia;
      await api.put(`/whatsapp/canal/${slug}`, {
        ...resto,
        wa_numeros_prueba: aLista(numeros_prueba_txt),
        wa_no_contestar: aLista(no_contestar_txt),
      });
      toast.success("Guardado.");
      setListaPor((l) => ({ ...l, [slug]: null })); // lo guardado cambio: la lista se vuelve a revisar
      await cargar();
      onCambio?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(null);
    }
  };

  const verificar = async (slug) => {
    ocupar(slug, "meta");
    try {
      const { data } = await api.get(`/whatsapp/canal/${slug}/meta`);
      setMetaPor((m) => ({ ...m, [slug]: data }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      ocupar(slug, null);
    }
  };

  const revisarLista = async (slug) => {
    ocupar(slug, "lista");
    try {
      const { data } = await api.get(`/whatsapp/listo/${slug}`);
      setListaPor((l) => ({ ...l, [slug]: data }));
      setMetaPor((m) => ({ ...m, [slug]: data.meta }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      ocupar(slug, null);
    }
  };

  const conectar = async (slug) => {
    ocupar(slug, "conectar");
    try {
      const { data } = await api.post(`/whatsapp/canal/${slug}/conectar`);
      toast.success(data.mensaje || "Conectado.");
      setMetaPor((m) => ({ ...m, [slug]: data.meta }));
      setListaPor((l) => ({ ...l, [slug]: null }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      ocupar(slug, null);
    }
  };

  const crearPlantilla = async (slug) => {
    ocupar(slug, "plantilla");
    try {
      const { data } = await api.post(`/whatsapp/canal/${slug}/plantilla-recordatorio`);
      const fallidas = (data.resultados || []).filter((r) => !r.ok);
      if (fallidas.length) toast.warning(fallidas.map((r) => `${r.idioma}: ${r.mensaje}`).join(" / "));
      else toast.success("Plantilla enviada a Meta. Suele aprobarse en minutos.");
      setMetaPor((m) => ({ ...m, [slug]: data.meta }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      ocupar(slug, null);
    }
  };

  const probarIa = async () => {
    setProbandoIa(true);
    try {
      const { data } = await api.post("/whatsapp/ia/probar");
      setIa(data);
      if (data.ok) toast.success(`La IA contesta bien (${data.segundos} s).`);
      else toast.error(data.mensaje || "La IA no contestó.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setProbandoIa(false);
    }
  };

  const set = (slug, campo, valor) =>
    setBorrador((b) => ({ ...b, [slug]: { ...(b[slug] || {}), [campo]: valor } }));

  // PROMPT 119: prender o apagar un dia del horario de atencion.
  const toggleDia = (slug, i) =>
    setBorrador((b) => {
      const actual = b[slug]?.wa_horario_dias || [];
      const dias = actual.includes(i) ? actual.filter((d) => d !== i) : [...actual, i].sort((x, y) => x - y);
      return { ...b, [slug]: { ...(b[slug] || {}), wa_horario_dias: dias } };
    });

  const urlCompleta =
    typeof window !== "undefined" && datos?.url_webhook
      ? `${window.location.origin}${datos.url_webhook}`
      : datos?.url_webhook || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="canal-whatsapp-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center justify-between gap-2 pr-6 flex-wrap">
            <span className="flex items-center gap-2">
              <Radio className="h-5 w-5" /> Canal de WhatsApp
            </span>
            <span className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={probarIa} disabled={probandoIa} data-testid="canal-probar-ia" className="gap-2">
                {probandoIa ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Probar la IA
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={cargar} data-testid="canal-refrescar" className="gap-2">
                <RefreshCw className="h-4 w-4" /> Actualizar
              </Button>
            </span>
          </DialogTitle>
          <DialogDescription>
            Conexión con WhatsApp de verdad. Mientras no estén cargadas las credenciales, el agente sigue
            funcionando y deja anotado lo que habría enviado, sin mandar nada.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : !datos ? null : (
          <div className="space-y-4">
            <Card className="p-3 space-y-2" data-testid="canal-resumen">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-slate-900">
                  {datos.modo === "real" ? "Enviando por WhatsApp" : "Modo simulación"}
                </span>
                <Chip ok={datos.token_cargado}>{datos.variables?.token}</Chip>
                <Chip ok={datos.app_secret_cargado}>{datos.variables?.app_secret}</Chip>
                <Chip ok={datos.verify_token_cargado}>{datos.variables?.verify_token}</Chip>
              </div>
              {ia ? (
                <div data-testid="canal-ia-resultado">
                  <Fila estado={ia.ok ? "ok" : "falta"} titulo="IA">
                    {ia.ok ? `contesta bien (${ia.segundos} s)` : ia.mensaje}
                  </Fila>
                </div>
              ) : null}
              <div>
                <Label className="text-xs text-slate-500">URL del webhook (se pega en el panel de Meta)</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={urlCompleta} className="font-mono text-xs" data-testid="canal-url" />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { navigator.clipboard?.writeText(urlCompleta); toast.success("Copiada."); }}
                  >
                    Copiar
                  </Button>
                </div>
              </div>
              <p className="text-[11px] text-slate-400">
                Las tres credenciales se cargan como variables de entorno en el servidor. Acá solo se ve si están
                cargadas, nunca su contenido.
              </p>
              <p className="text-[11px] text-slate-500" data-testid="canal-como-conectar">
                Para conectar el número real: cargá en la marca su Phone number ID, el número y el WABA ID (están en
                WhatsApp Manager), dejá el agente en "Nunca" y guardá; después tocá "Verificar con Meta" y "Conectar".
                Desde ahí los mensajes entran a la plataforma y nadie contesta solo hasta que lo decidas.
              </p>
            </Card>
            {(datos.marcas || []).map((m) => {
              const b = borrador[m.slug] || {};
              const que = ocupado[m.slug];
              const pruebas = aLista(b.numeros_prueba_txt).length;
              // PROMPT 144: lo que dijo Meta de la coexistencia (despues de "Verificar con Meta") contra la casilla.
              const metaCx = metaPor[m.slug]?.numero?.coexistencia;
              const cxDistinta = typeof metaCx === "boolean" && typeof b.wa_coexistencia === "boolean" && metaCx !== b.wa_coexistencia;
              const colorMalEscrito = !!b.wa_color && !esColorValido(b.wa_color);
              return (
                <Card key={m.slug} className="p-3 space-y-2" data-testid={`canal-marca-${m.slug}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">{m.remitente_nombre || m.nombre}</span>
                    <div className="flex items-center gap-2">
                      <Chip ok={m.listo}>{m.listo ? "Número listo" : "Falta el número"}</Chip>
                      <label
                        className="flex items-center gap-1.5 text-xs text-slate-600"
                        title="Marca el número como conectado. Si el agente contesta solo se elige abajo."
                      >
                        <input
                          type="checkbox"
                          checked={!!b.wa_activo}
                          onChange={(e) => set(m.slug, "wa_activo", e.target.checked)}
                          data-testid={`canal-activo-${m.slug}`}
                        />
                        Número activo
                      </label>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs text-slate-500">Phone number ID</Label>
                      <Input
                        value={b.wa_phone_number_id ?? ""}
                        onChange={(e) => set(m.slug, "wa_phone_number_id", e.target.value)}
                        placeholder="del panel de Meta"
                        className="font-mono text-xs"
                        data-testid={`canal-numero-${m.slug}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Número que ve el cliente</Label>
                      <Input
                        value={b.wa_display_number ?? ""}
                        onChange={(e) => set(m.slug, "wa_display_number", e.target.value)}
                        placeholder="+1 555 123 4567"
                        className="text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">WABA ID</Label>
                      <Input
                        value={b.wa_waba_id ?? ""}
                        onChange={(e) => set(m.slug, "wa_waba_id", e.target.value)}
                        placeholder="para verificar y conectar"
                        className="font-mono text-xs"
                        data-testid={`canal-waba-${m.slug}`}
                      />
                    </div>
                  </div>
                  {/* PROMPT 144: el color de la marca en el chat y si el numero tambien se usa en la app del celular. */}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid={`canal-estilo-${m.slug}`}>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Color en el chat</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={esColorValido(b.wa_color) ? b.wa_color.toUpperCase() : COLOR_OTRAS_MARCAS}
                          onChange={(e) => set(m.slug, "wa_color", e.target.value.toUpperCase())}
                          aria-label={`Color de ${m.remitente_nombre || m.nombre} en el chat`}
                          className="h-9 w-12 cursor-pointer rounded-md border border-slate-200 bg-white p-1"
                          data-testid={`canal-color-${m.slug}`}
                        />
                        <Input
                          value={b.wa_color ?? ""}
                          onChange={(e) => set(m.slug, "wa_color", e.target.value)}
                          placeholder="#D4A017"
                          maxLength={7}
                          className={`w-28 font-mono text-xs ${colorMalEscrito ? "border-red-400" : ""}`}
                          data-testid={`canal-color-codigo-${m.slug}`}
                        />
                      </div>
                      {colorMalEscrito && (
                        <p className="text-[11px] text-red-600" data-testid={`canal-color-error-${m.slug}`}>
                          El color tiene que ser #RRGGBB, por ejemplo #D4A017.
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <label className="flex items-start gap-2 text-xs text-slate-700">
                        <input
                          type="checkbox"
                          checked={b.wa_coexistencia === true}
                          onChange={(e) => set(m.slug, "wa_coexistencia", e.target.checked)}
                          className="mt-0.5"
                          data-testid={`canal-coexistencia-${m.slug}`}
                        />
                        <span>Este número también se usa en la app WhatsApp Business del celular (coexistencia)</span>
                      </label>
                      {typeof metaCx === "boolean" && (
                        <p
                          className={`text-[11px] ${cxDistinta ? "text-amber-700" : "text-slate-500"}`}
                          data-testid={`canal-coexistencia-meta-${m.slug}`}
                        >
                          Meta dice: {metaCx ? "sí, también está en la app del celular" : "no, solo funciona con la API"}.
                          {cxDistinta ? " No coincide con la casilla: revisá en Meta o cambiá la casilla." : ""}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="flex items-start gap-2 text-xs text-slate-700">
                      <input
                        type="checkbox"
                        checked={b.wa_marcar_leido === true}
                        onChange={(e) => set(m.slug, "wa_marcar_leido", e.target.checked)}
                        className="mt-0.5"
                        data-testid={`canal-marcar-leido-${m.slug}`}
                      />
                      <span>Avisarle al cliente que leímos su mensaje y que estamos escribiendo</span>
                    </label>
                    <p
                      className="text-[11px] text-slate-500"
                      data-testid={`canal-marcar-leido-ayuda-${m.slug}`}
                    >
                      Pensado para los números que se usan solo desde la plataforma. Si el número
                      también está en el celular, el mensaje puede quedar como leído también allá.
                      Viene apagado.
                    </p>
                  </div>
                  <PanelConsumo slug={m.slug} />
                  {/* PROMPT 119: con la coexistencia el número es el mismo que el equipo usa en el
                      celular, así que el agente solo contesta cuando la marca lo decide. */}
                  <div className="rounded-md border border-slate-200 bg-slate-50/60 p-2 space-y-2" data-testid={`canal-agente-${m.slug}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Label className="text-xs text-slate-600">El agente contesta solo</Label>
                      <select
                        value={b.wa_agente_modo || "nunca"}
                        onChange={(e) => set(m.slug, "wa_agente_modo", e.target.value)}
                        className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700"
                        data-testid={`canal-modo-${m.slug}`}
                      >
                        <option value="nunca">Nunca</option>
                        <option value="prueba">Prueba: solo a los números de prueba</option>
                        <option value="siempre">Siempre</option>
                        <option value="fuera_de_horario">Solo fuera del horario de atención</option>
                      </select>
                    </div>
                    {/* PROMPT 120: con lo que esta GUARDADO (no con lo que se esta editando), si el
                        agente contesta solo en este momento. */}
                    {m.listo && typeof m.contesta_ahora === "boolean" && (
                      <p className="text-[11px] text-slate-600" data-testid={`canal-ahora-${m.slug}`}>
                        Con lo guardado, ahora
                        {datos.ahora_negocio ? ` (${DIAS_LARGOS[datos.ahora_negocio.dia] || ""} ${datos.ahora_negocio.hora}, hora de Nueva York)` : ""}{" "}
                        el agente{" "}
                        <strong>
                          {m.wa_agente_modo === "prueba"
                            ? `contesta solo a ${(m.wa_numeros_prueba || []).length} número(s) de prueba`
                            : m.contesta_ahora
                            ? "contesta solo"
                            : "no contesta solo"}
                        </strong>
                        .
                      </p>
                    )}
                    {/* Fix 155 (2026-10-22): el horario tambien se edita con la Ausencia activa (PROMPT 150), no solo con el agente "fuera de horario". */}
                    {(b.wa_agente_modo === "fuera_de_horario" || b.wa_ausencia?.activa) && (
                      <div className="flex items-center gap-2 flex-wrap text-xs text-slate-600" data-testid={`canal-horario-${m.slug}`}>
                        <span>Horario de atención (hora de Nueva York): de</span>
                        <Input
                          type="time"
                          value={b.wa_horario_desde || "09:00"}
                          onChange={(e) => set(m.slug, "wa_horario_desde", e.target.value)}
                          className="h-8 w-28 text-xs"
                          data-testid={`canal-desde-${m.slug}`}
                        />
                        <span>a</span>
                        <Input
                          type="time"
                          value={b.wa_horario_hasta || "18:00"}
                          onChange={(e) => set(m.slug, "wa_horario_hasta", e.target.value)}
                          className="h-8 w-28 text-xs"
                          data-testid={`canal-hasta-${m.slug}`}
                        />
                        <div className="flex gap-1">
                          {DIAS.map((d, i) => {
                            const on = (b.wa_horario_dias || []).includes(i);
                            return (
                              <button
                                type="button"
                                key={d}
                                onClick={() => toggleDia(m.slug, i)}
                                className={`h-7 w-7 rounded text-[11px] font-medium border ${
                                  on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-200"
                                }`}
                                data-testid={`canal-dia-${m.slug}-${i}`}
                              >
                                {d}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {/* PROMPT 127: a quién le contesta en el modo prueba y a quién nunca. */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {(b.wa_agente_modo === "prueba" || pruebas > 0) && (
                        <div>
                          <Label className="text-xs text-slate-600">Números de prueba ({pruebas})</Label>
                          <Textarea
                            value={b.numeros_prueba_txt || ""}
                            onChange={(e) => set(m.slug, "numeros_prueba_txt", e.target.value)}
                            placeholder={"+54 9 11 1234 5678\nuno por renglón, con código de país"}
                            className="mt-1 min-h-[64px] font-mono text-xs bg-white"
                            data-testid={`canal-prueba-${m.slug}`}
                          />
                          <p className="mt-0.5 text-[11px] text-slate-500">En "Prueba" el agente les contesta solo a estos números.</p>
                        </div>
                      )}
                      <div>
                        <Label className="text-xs text-slate-600">No contestar nunca ({aLista(b.no_contestar_txt).length})</Label>
                        <Textarea
                          value={b.no_contestar_txt || ""}
                          onChange={(e) => set(m.slug, "no_contestar_txt", e.target.value)}
                          placeholder={"+1 201 555 0100\nuno por renglón, con código de país"}
                          className="mt-1 min-h-[64px] font-mono text-xs bg-white"
                          data-testid={`canal-no-contestar-${m.slug}`}
                        />
                        <p className="mt-0.5 text-[11px] text-slate-500">
                          Agencias, guías, proveedores y gente del equipo: el agente nunca les contesta, en ningún modo.
                        </p>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-500">
                      Cuando no contesta, los mensajes igual entran a la plataforma y no se marcan como leídos. Si alguien
                      del equipo le escribe al cliente desde el celular, en esa conversación el agente deja de contestar.
                    </p>
                  </div>
                  <MensajesAutomaticos slug={m.slug} b={b} set={set} editable={isAdmin} />
                  <PanelMeta
                    meta={metaPor[m.slug]}
                    slug={m.slug}
                    isAdmin={isAdmin}
                    conectando={que === "conectar"}
                    creando={que === "plantilla"}
                    onConectar={() => conectar(m.slug)}
                    onCrearPlantilla={() => crearPlantilla(m.slug)}
                  />
                  <PanelLista lista={listaPor[m.slug]} slug={m.slug} />
                  <div className="flex justify-between gap-2 flex-wrap">
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => verificar(m.slug)}
                        disabled={!!que || !m.wa_phone_number_id}
                        title={m.wa_phone_number_id ? "Le pregunta a Meta por este número (no cambia nada)" : "Primero cargá y guardá el número"}
                        data-testid={`canal-verificar-${m.slug}`}
                        className="gap-1.5"
                      >
                        {que === "meta" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                        Verificar con Meta
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => revisarLista(m.slug)}
                        disabled={!!que}
                        title="Revisa todo lo que tiene que estar bien para prender el agente (no cambia nada)"
                        data-testid={`canal-listo-${m.slug}`}
                        className="gap-1.5"
                      >
                        {que === "lista" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardCheck className="h-3.5 w-3.5" />}
                        Lista para prender
                      </Button>
                      <BotonPerfilWhatsApp slug={m.slug} deshabilitado={!m.wa_phone_number_id} />
                    </div>
                    <Button
                      size="sm"
                      onClick={() => guardar(m.slug)}
                      disabled={guardando === m.slug || !isAdmin}
                      title={isAdmin ? undefined : "Solo un administrador puede cambiar el canal"}
                      data-testid={`canal-guardar-${m.slug}`}
                      className="gap-1.5"
                    >
                      {guardando === m.slug ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                      Guardar
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
